import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Card,
  CardBody,
  Chip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  Skeleton,
  StarRating,
  Tab,
  Tabs,
  softBadgeColors,
} from '@structural-climbing/ui'
import { disciplineForSection, gradeDisplay } from '@structural-climbing/validation'
import { useGymGradeConfig } from '../../layouts/useGymGradeConfig'
import { useGymThemeContext } from '../../layouts/useGymTheme'
import { MemberIdentity } from '../members/MemberIdentity'
import { MemberProfileModal } from '../members/MemberProfileModal'
import { CommentIcon } from './CommentIcon'
import { useRouteComments, type RouteComment } from './useRouteComments'
import type { ClimbingRoute } from './types'

type Sort = 'newest' | 'lowest' | 'highest'

interface Props {
  route: ClimbingRoute | null
  apiUrl: string
  onClose: () => void
}

/**
 * Unrated comments sink to the bottom of either rating sort: a setter sorting
 * by stars wants the stars first. Ties fall back to newest.
 */
export function sortComments(comments: RouteComment[], sort: Sort): RouteComment[] {
  const newest = (a: RouteComment, b: RouteComment) => b.created_at.localeCompare(a.created_at)
  if (sort === 'newest') return [...comments].sort(newest)
  const dir = sort === 'lowest' ? 1 : -1
  return [...comments].sort((a, b) => {
    if (a.rating == null && b.rating == null) return newest(a, b)
    if (a.rating == null) return 1
    if (b.rating == null) return -1
    return (a.rating - b.rating) * dir || newest(a, b)
  })
}

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 86_400],
  ['month', 30 * 86_400],
  ['week', 7 * 86_400],
  ['day', 86_400],
  ['hour', 3_600],
  ['minute', 60],
]

function timeAgo(iso: string, locale: string): string {
  const seconds = (new Date(iso).getTime() - Date.now()) / 1000
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  for (const [unit, size] of RELATIVE_UNITS) {
    if (Math.abs(seconds) >= size) return rtf.format(Math.round(seconds / size), unit)
  }
  return rtf.format(0, 'minute')
}

function displayName(c: RouteComment): string {
  return [c.profile?.first_name, c.profile?.last_name].filter(Boolean).join(' ').trim()
}

/**
 * Every climber's note on one route, for the setters: who said it, how they
 * climbed it, what they thought of it.
 */
export function RouteCommentsModal({ route, apiUrl, onClose }: Props) {
  const { t, i18n } = useTranslation()
  const gradeConfig = useGymGradeConfig()
  const { primaryColor } = useGymThemeContext()
  const { comments, loading, error } = useRouteComments(apiUrl, route?.id ?? null)
  const [sort, setSort] = useState<Sort>('newest')
  const [profileTarget, setProfileTarget] = useState<string | null>(null)

  const sorted = useMemo(() => sortComments(comments, sort), [comments, sort])

  // The stars of the people who commented, not the route's overall average:
  // it is the mood of this list.
  const rated = comments.filter((c) => c.rating != null)
  const commentersAvg = rated.length
    ? Math.round((rated.reduce((s, c) => s + (c.rating ?? 0), 0) / rated.length) * 10) / 10
    : null

  return (
    <>
      <Modal
        isOpen={!!route}
        onOpenChange={(open) => {
          if (!open) {
            setSort('newest')
            onClose()
          }
        }}
        size="lg"
        scrollBehavior="inside"
      >
        <ModalContent>
          {() => {
            if (!route) return null
            const grade = gradeDisplay(
              gradeConfig,
              route.grade,
              disciplineForSection(route.section?.discipline),
            )
            const badge = softBadgeColors(grade.color)
            return (
              <>
                <ModalHeader className="flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 flex-none overflow-hidden rounded-lg bg-default-100">
                      {route.image_r2_key && (
                        <img
                          src={`${apiUrl}/climbing-routes/${route.id}/image?size=thumb`}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      )}
                    </div>
                    <div className="flex min-w-0 flex-col gap-1">
                      <span className="text-base font-semibold">{t('routes.commentsTitle')}</span>
                      <div className="flex flex-wrap items-center gap-2 text-sm font-normal">
                        <span
                          className="h-3.5 w-3.5 flex-none rounded-full border border-black/10"
                          style={{ backgroundColor: route.color ?? '#d4d4d8' }}
                        />
                        <Chip
                          size="sm"
                          radius="sm"
                          className="font-bold"
                          style={{ backgroundColor: badge.bg, color: badge.fg }}
                        >
                          {grade.label}
                        </Chip>
                        {route.section && (
                          <Chip size="sm" variant="flat">
                            {route.section.name}
                          </Chip>
                        )}
                        {route.setter?.name && (
                          <span className="truncate text-xs text-foreground/50">
                            {t('routes.setterLabel', { name: route.setter.name })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {!loading && comments.length > 0 && (
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p
                        className="flex items-center gap-2 text-sm font-normal text-foreground/60"
                        data-testid="comments-summary"
                      >
                        {t('routes.commentsCount', { count: comments.length })}
                        {commentersAvg != null && (
                          <>
                            <span aria-hidden>·</span>
                            <StarRating
                              value={commentersAvg}
                              showValue
                              size="sm"
                              label={t('routes.ratingOutOf', { value: commentersAvg.toFixed(1) })}
                            />
                          </>
                        )}
                      </p>
                      {comments.length > 1 && (
                        <Tabs
                          size="sm"
                          aria-label={t('routes.commentsSort')}
                          selectedKey={sort}
                          onSelectionChange={(key) => setSort(key as Sort)}
                        >
                          <Tab key="newest" title={t('routes.commentsSortNewest')} />
                          <Tab key="lowest" title={t('routes.commentsSortLowest')} />
                          <Tab key="highest" title={t('routes.commentsSortHighest')} />
                        </Tabs>
                      )}
                    </div>
                  )}
                </ModalHeader>

                <ModalBody className="pb-6">
                  {loading ? (
                    <div className="flex flex-col gap-3" data-testid="comments-loading">
                      {[0, 1, 2].map((i) => (
                        <Card key={i} shadow="none" className="bg-content2">
                          <CardBody className="flex flex-col gap-3 p-4">
                            <div className="flex items-center gap-2">
                              <Skeleton className="h-8 w-8 rounded-full" />
                              <Skeleton className="h-3 w-32 rounded-lg" />
                            </div>
                            <Skeleton className="h-3 w-full rounded-lg" />
                            <Skeleton className="h-3 w-2/3 rounded-lg" />
                          </CardBody>
                        </Card>
                      ))}
                    </div>
                  ) : error ? (
                    <p className="py-10 text-center text-sm text-danger">
                      {t('routes.commentsLoadFailed')}
                    </p>
                  ) : comments.length === 0 ? (
                    <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-default-100 text-foreground/30">
                        <CommentIcon className="h-6 w-6" />
                      </div>
                      <p className="text-sm font-medium">{t('routes.commentsEmptyTitle')}</p>
                      <p className="max-w-xs text-sm text-foreground/50">
                        {t('routes.commentsEmptyBody')}
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3" aria-label={t('routes.commentsTitle')}>
                      {sorted.map((c) => {
                        const edited =
                          new Date(c.updated_at).getTime() - new Date(c.created_at).getTime() >
                          60_000
                        return (
                          <Card
                            key={c.id}
                            shadow="none"
                            className="bg-content2"
                            data-testid="route-comment"
                          >
                            <CardBody className="flex flex-col gap-3 p-4">
                              <div className="flex flex-wrap items-start justify-between gap-2">
                                <Button
                                  variant="light"
                                  className="h-auto min-w-0 justify-start px-1 py-1 text-left"
                                  onPress={() => setProfileTarget(c.user_id)}
                                  title={t('members.viewProfile')}
                                >
                                  <MemberIdentity
                                    userId={c.user_id}
                                    name={displayName(c)}
                                    nickname={c.profile?.nickname}
                                    email={c.profile?.email}
                                    avatarExt={c.profile?.avatar_ext}
                                    avatarUpdatedAt={c.profile?.avatar_updated_at}
                                    primaryColor={primaryColor}
                                  />
                                </Button>
                                <div className="flex flex-none items-center gap-2">
                                  {c.attempts === 1 ? (
                                    <Chip
                                      size="sm"
                                      variant="flat"
                                      color="warning"
                                      className="font-bold uppercase"
                                    >
                                      {t('routes.commentFlash')}
                                    </Chip>
                                  ) : c.attempts != null ? (
                                    <Chip size="sm" variant="flat">
                                      {c.attempts >= 4
                                        ? t('routes.commentAttemptsMore')
                                        : t('routes.commentAttempts', { count: c.attempts })}
                                    </Chip>
                                  ) : null}
                                  {c.rating != null && (
                                    <StarRating
                                      value={c.rating}
                                      size="sm"
                                      label={t('routes.ratingOutOf', { value: c.rating })}
                                    />
                                  )}
                                </div>
                              </div>
                              <p className="whitespace-pre-line break-words text-sm leading-relaxed text-foreground/90">
                                {c.body}
                              </p>
                              <p
                                className="text-xs text-foreground/40"
                                title={new Date(c.created_at).toLocaleString(i18n.language)}
                              >
                                {timeAgo(c.created_at, i18n.language)}
                                {edited && ` · ${t('routes.commentEdited')}`}
                              </p>
                            </CardBody>
                          </Card>
                        )
                      })}
                    </div>
                  )}
                </ModalBody>
              </>
            )
          }}
        </ModalContent>
      </Modal>

      {profileTarget && (
        <MemberProfileModal userId={profileTarget} onClose={() => setProfileTarget(null)} />
      )}
    </>
  )
}
